import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import SignatureForm from '../components/SignatureForm'
import TagHeader from '../components/TagHeader'
import { useTagContext } from '../contexts/TagContext'
import { notifySupervisor, setSiteSupervisor } from '../services/tagService'
import type { User } from '../types/user'

interface CustomSupervisor {
  name: string
  email: string
  phone?: string
}

const SignConfirmation: React.FC = () => {
  const { tagId } = useParams()
  const navigate = useNavigate()
  const {
    getCurrentTag,
    isOffline,
    completeTagWithSignature,
    submitDriverSignature,
    refreshTag,
  } = useTagContext()
  const currentTag = getCurrentTag(tagId)
  const [selectedSiteSupervisor, setSelectedSiteSupervisor] =
    useState<User | null>(currentTag?.site_supervisor || null)
  const [customSupervisor, setCustomSupervisor] =
    useState<CustomSupervisor | null>(null)
  const [supervisorSelectionMode, setSupervisorSelectionMode] = useState<
    'assigned' | 'custom'
  >('assigned')

  // Update selected supervisor when tag changes
  useEffect(() => {
    if (currentTag?.site_supervisor) {
      setSelectedSiteSupervisor(currentTag.site_supervisor)
    }
  }, [currentTag])

  const handleSubmit = async (data: {
    supervisorSignature: string
    driverSignature: string
    sharedEmails: string[]
    selectedSiteSupervisor?: User | null
    customSupervisor?: CustomSupervisor | null
    supervisorSelectionMode: 'assigned' | 'custom'
  }) => {
    if (!tagId) {
      toast.error('Tag ID is required')
      return
    }

    try {
      // Validate supervisor selection based on mode
      if (
        data.supervisorSelectionMode === 'assigned' &&
        !data.selectedSiteSupervisor
      ) {
        toast.error('Please select a site supervisor')
        return
      }

      if (data.supervisorSelectionMode === 'custom' && !data.customSupervisor) {
        toast.error('Please enter custom supervisor details')
        return
      }

      if (
        data.supervisorSelectionMode === 'custom' &&
        (!data.customSupervisor?.name || !data.customSupervisor?.email)
      ) {
        toast.error('Please enter supervisor name and email')
        return
      }

      // Assign the selected supervisor to the tag (for assigned mode)
      if (
        data.supervisorSelectionMode === 'assigned' &&
        data.selectedSiteSupervisor
      ) {
        await setSiteSupervisor(tagId, data.selectedSiteSupervisor.id)
        // Refresh the tag so UI reflects the assigned supervisor
        await refreshTag(tagId)
        await notifySupervisor(tagId, {
          supervisorSelectionMode: 'assigned',
          siteSupervisorId: data.selectedSiteSupervisor.id,
          notificationType: 'link',
        })
        console.log(
          'Assigned supervisor set and notification sent:',
          data.selectedSiteSupervisor,
        )
      } else if (
        data.supervisorSelectionMode === 'custom' &&
        data.customSupervisor
      ) {
        await notifySupervisor(tagId, {
          supervisorSelectionMode: 'custom',
          customSupervisor: data.customSupervisor,
          notificationType: 'link',
        })
        console.log(
          'Custom supervisor notification sent:',
          data.customSupervisor,
        )
      }

      // Submit both signatures
      if (isOffline) {
        await completeTagWithSignature(tagId, data.supervisorSignature)
        await submitDriverSignature(tagId, data.driverSignature)
        navigate('/tags/completed/')
        toast.success('Changes will sync when back online', {
          style: { color: '#31C48D' },
        })
      } else {
        await completeTagWithSignature(tagId, data.supervisorSignature)
        await submitDriverSignature(tagId, data.driverSignature)
        navigate('/tags/completed/')
        toast.success('Tag completed successfully', {
          style: { color: '#31C48D' },
        })
      }
    } catch (error) {
      console.error('Failed to process signatures:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to submit signatures. Please try again.',
        { style: { color: '#721c24' } },
      )
    }
  }

  const isFormValid = (data: {
    supervisorSignature: string
    driverSignature: string
    selectedSiteSupervisor?: User | null
    customSupervisor?: CustomSupervisor | null
    supervisorSelectionMode: 'assigned' | 'custom'
  }): boolean => {
    // Both signatures are required
    if (!data.supervisorSignature || !data.driverSignature) {
      return false
    }

    // Supervisor validation based on mode
    if (data.supervisorSelectionMode === 'assigned') {
      return !!data.selectedSiteSupervisor
    } else if (data.supervisorSelectionMode === 'custom') {
      return !!(data.customSupervisor?.name && data.customSupervisor?.email)
    }

    return false
  }

  return (
    <div style={{ width: '100%' }}>
      <TagHeader
        title="Site Supervisor"
        status={currentTag?.status || 'IN_PROGRESS'}
        tagId={tagId}
      />

      <SignatureForm
        confirmationText="I confirm loads have been received:"
        selectedSiteSupervisor={selectedSiteSupervisor}
        projectSiteSupervisors={currentTag?.project_site_supervisors || []}
        onSiteSupervisorChange={setSelectedSiteSupervisor}
        customSupervisor={customSupervisor}
        onCustomSupervisorChange={setCustomSupervisor}
        supervisorSelectionMode={supervisorSelectionMode}
        onSupervisorModeChange={setSupervisorSelectionMode}
        submitButtonText="Accept & Send"
        onSubmit={handleSubmit}
        isValid={isFormValid}
        requireBothSignatures={true}
      />
    </div>
  )
}

export default SignConfirmation
